/**
 * This hook sets "aria-hidden" to "true" for all content except for the ref element.
 */
import * as React from "react"

type HiddenElement = [Element, string | null]
type DefaultProps = Partial<typeof defaultProps>
type Props = {
  ref: React.RefObject<HTMLElement | null>
} & DefaultProps

const isBody = ({ localName }: Element) => localName === "body"
const isHidden = (element: Element) => {
  const hidden = element.getAttribute("aria-hidden")
  return hidden !== null && hidden !== "false"
}

const getSiblings = (element: Element) => {
  const parent = element.parentNode
  if (parent === null) {
    return []
  }
  return Array.from(parent.children).filter(el => el !== element)
}

const hideSiblings = (element: Element) => {
  const siblings = getSiblings(element).filter(el => !isHidden(el))
  const originals = siblings.map((el: Element) => [
    el,
    el.getAttribute("aria-hidden")
  ]) as [Element, string | null][]
  siblings.forEach(el => {
    el.setAttribute("aria-hidden", "true")
  })
  return originals
}

const defaultProps = {
  disabled: false
}

const usePerceptionTrap = (props: Props) => {
  const { ref, disabled } = { ...defaultProps, ...props }
  const changedElements: HiddenElement[] = []

  React.useEffect(() => {
    if (ref.current === null || disabled) {
      return
    }
    let el: HTMLElement | null = ref.current
    while (el && !isBody(el)) {
      hideSiblings(el).forEach(x => changedElements.push(x))
      el = el.parentElement
    }

    return () => {
      changedElements.forEach(([el, val]) => {
        val === null
          ? el.removeAttribute("aria-hidden")
          : el.setAttribute("aria-hidden", val)
      })
    }
  }, [changedElements, disabled, ref])
}

export default usePerceptionTrap
